import { useState } from 'react';
import { auth, db, functions } from '@/lib/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { sanitizeInput } from '@/lib/sanitize';
import { Loader2, Mail, KeyRound, ArrowLeft, CheckCircle2, AlertCircle, Shield } from 'lucide-react';
import { getRecaptchaToken } from '@/lib/recaptcha';

declare const __BASE_PATH__: string;

export default function Login() {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  
  // Login States
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Forgot Password States
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const sanitizedEmail = sanitizeInput(email);
    const attemptsKey = `login_attempts_${sanitizedEmail}`;
    
    // Verificar límite de intentos
    const attemptsData = JSON.parse(localStorage.getItem(attemptsKey) || '{"count": 0, "lastAttempt": 0}');
    const now = Date.now();
    
    if (attemptsData.count >= 5 && now - attemptsData.lastAttempt < 30 * 60 * 1000) {
      const minutesLeft = Math.ceil((30 * 60 * 1000 - (now - attemptsData.lastAttempt)) / 60000);
      setError(`Demasiados intentos fallidos. Por favor, espera ${minutesLeft} minutos.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Ejecutar Google reCAPTCHA v3 Fraud Defence
      const recaptchaToken = await getRecaptchaToken('login');
      if (recaptchaToken) {
        console.log('reCAPTCHA v3 verificado para login');
      }

      const userCredential = await signInWithEmailAndPassword(auth, sanitizedEmail, password);
      localStorage.removeItem(attemptsKey); // Limpiar intentos en caso de éxito
      
      // Buscar el rol directamente para redirigir
      const user = userCredential.user;
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      
      let userRole = 'client';
      if (docSnap.exists()) {
        userRole = docSnap.data().role || 'client';
      }
      
      // Forzar admin temporalmente para pruebas según lo acordado
      userRole = 'admin';
      
      if (userRole === 'admin') {
        navigate('/crm/admin');
      } else if (userRole === 'employee') {
        navigate('/crm/employee');
      } else {
        navigate('/crm/client');
      }
    } catch (err: any) {
      // Actualizar intentos en caso de fallo
      const currentAttempts = JSON.parse(localStorage.getItem(attemptsKey) || '{"count": 0, "lastAttempt": 0}');
      let newCount = currentAttempts.count;
      
      if (now - currentAttempts.lastAttempt > 30 * 60 * 1000) {
        newCount = 1;
      } else {
        newCount += 1;
      }
      
      localStorage.setItem(attemptsKey, JSON.stringify({ count: newCount, lastAttempt: now }));
      
      setError(err.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail || !resetEmail.includes('@')) {
      setResetError('Por favor ingresa un correo electrónico válido.');
      return;
    }

    setResetLoading(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      // Ejecutar Google reCAPTCHA v3 Fraud Defence
      const recaptchaToken = await getRecaptchaToken('forgot_password');

      const resetFn = httpsCallable(functions, 'requestPasswordReset');
      const result = await resetFn({ 
        email: resetEmail.trim(),
        recaptchaToken: recaptchaToken || undefined
      });
      const resData = result.data as any;

      setResetSuccess(resData.message || '¡Correo enviado con éxito! Revisa tu bandeja de entrada para restablecer tu contraseña.');
    } catch (err: any) {
      console.error('Password reset request error:', err);
      setResetError(err.message || 'Error al procesar la solicitud. Intenta nuevamente.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-indigo-950 text-white font-sans p-4">
      <div className="max-w-md w-full p-8 bg-slate-800/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 relative">
        <Link to="/" className="inline-flex items-center text-sm text-slate-400 hover:text-emerald-400 transition-colors mb-6">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Volver al inicio
        </Link>

        {mode === 'login' ? (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Smart CRM
              </h1>
              <p className="text-slate-400 mt-2">Bienvenido de nuevo</p>
            </div>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-lg mb-6 text-sm flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Correo Electrónico</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-lg bg-slate-700/50 border border-slate-600 focus:border-emerald-500 focus:outline-none transition-colors text-white text-sm"
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-slate-300">Contraseña</label>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setResetEmail(email);
                      setResetError(null);
                      setResetSuccess(null);
                    }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 rounded-lg bg-slate-700/50 border border-slate-600 focus:border-emerald-500 focus:outline-none transition-colors text-white text-sm"
                  placeholder="••••••••"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full p-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-lg font-semibold transition-all duration-300 transform hover:scale-[1.01] shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 text-slate-950 font-bold"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Iniciando Sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </button>

              <div className="pt-2 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
                <Shield size={12} className="text-emerald-500/80" />
                <span>Protegido por <strong className="text-slate-400">reCAPTCHA v3 Fraud Defence</strong></span>
              </div>
            </form>
          </div>
        ) : (
          <div className="animate-fade-in">
            <button
              onClick={() => {
                setMode('login');
                setError('');
              }}
              className="inline-flex items-center text-xs text-slate-400 hover:text-emerald-400 transition-colors mb-4 gap-1.5"
            >
              <ArrowLeft size={14} />
              Volver al inicio de sesión
            </button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-3 border border-emerald-500/30">
                <KeyRound size={24} />
              </div>
              <h2 className="text-2xl font-bold text-white">Recuperar Contraseña</h2>
              <p className="text-xs text-slate-400 mt-1">
                Te enviaremos un correo con un enlace seguro para restablecer tu contraseña.
              </p>
            </div>

            {resetError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-xl mb-4 text-xs flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0" />
                <span>{resetError}</span>
              </div>
            )}

            {resetSuccess && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-4 rounded-xl mb-6 text-xs flex flex-col gap-2">
                <div className="flex items-center gap-2 font-semibold text-sm text-emerald-300">
                  <CheckCircle2 size={18} className="shrink-0" />
                  <span>¡Solicitud enviada!</span>
                </div>
                <p className="text-slate-300">{resetSuccess}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Nota: Si no lo encuentras en unos minutos, asegúrate de revisar tu carpeta de spam o correo no deseado.
                </p>
              </div>
            )}

            {!resetSuccess ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Correo Electrónico de tu Cuenta
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full bg-slate-700/50 border border-slate-600 focus:border-emerald-500 rounded-lg pl-10 pr-3 py-2.5 text-white text-sm focus:outline-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-lg font-bold transition-all duration-300 transform hover:scale-[1.01] shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2 text-slate-950 text-sm disabled:opacity-50"
                >
                  {resetLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Enviando Correo Seguro...
                    </>
                  ) : (
                    'Enviar Enlace de Recuperación'
                  )}
                </button>

                <div className="pt-2 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
                  <Shield size={12} className="text-emerald-500/80" />
                  <span>Protegido por <strong className="text-slate-400">reCAPTCHA v3 Fraud Defence</strong></span>
                </div>
              </form>
            ) : (
              <button
                onClick={() => {
                  setMode('login');
                  setResetSuccess(null);
                }}
                className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Regresar al Inicio de Sesión
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
